
import { useCallback, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { api } from "@/services/api";
import { useAgentStore } from "@/store/agentStore";
import type { ChatSessionData } from "@/services/api";
import "./ConversationSidebar.css";

export function ConversationSidebar() {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const savedSessions = useAgentStore((s) => s.savedSessions);
  const chatSessionId = useAgentStore((s) => s.chatSessionId);
  const newChat = useAgentStore((s) => s.newChat);
  const setSavedSessions = useAgentStore((s) => s.setSavedSessions);
  const loadSessionMessages = useAgentStore((s) => s.loadSessionMessages);
  const warehouseId = useAgentStore((s) => s.warehouseId);
  const userId = useAgentStore((s) => s.userId);

  const handleLoad = useCallback(
    async (session: ChatSessionData) => {
      if (chatSessionId === session.id) return;
      try {
        const msgs = await api.getSessionMessages(session.id);
        loadSessionMessages(session.id, msgs);
      } catch {
        // silently ignore — user still gets an empty chat
      }
    },
    [chatSessionId, loadSessionMessages]
  );

  const handleDelete = useCallback(
    async (e: React.MouseEvent, sessionId: string) => {
      e.stopPropagation();
      try {
        await api.deleteSession(sessionId, userId ?? undefined);
        const updated = savedSessions.filter((s) => s.id !== sessionId);
        setSavedSessions(updated);
        // If deleted session was active, start fresh
        if (chatSessionId === sessionId) newChat();
      } catch {
        // ignore
      }
    },
    [chatSessionId, newChat, savedSessions, setSavedSessions, userId]
  );

  const handleNewChat = useCallback(async () => {
    newChat();
    // Refresh sessions list to reflect any title changes
    try {
      const sessions = await api.listSessions({
        userId: userId ?? undefined,
        warehouseId: warehouseId ?? undefined,
      });
      setSavedSessions(sessions);
    } catch {
      // ignore
    }
  }, [newChat, setSavedSessions, userId, warehouseId]);

  return (
    <div className={`conv-sidebar${isCollapsed ? " conv-sidebar--collapsed" : ""}`}>
      <div className="conv-header">
        {!isCollapsed && <span className="conv-header-label">CHATS</span>}
        <div className="conv-header-actions">
          {!isCollapsed && (
            <button className="new-chat-btn" onClick={handleNewChat} title="New chat">
              +
            </button>
          )}
          <button
            className="conv-collapse-btn"
            onClick={() => setIsCollapsed((v) => !v)}
            title={isCollapsed ? "Expand" : "Collapse"}
          >
            {isCollapsed ? "›" : "‹"}
          </button>
        </div>
      </div>

      <div className="conv-list">
        {savedSessions.length === 0 ? (
          <div className="conv-empty">No saved chats yet</div>
        ) : (
          savedSessions.map((session) => {
            const isActive = chatSessionId === session.id;
            return (
              <button
                key={session.id}
                onClick={() => handleLoad(session)}
                className={`conv-item${isActive ? " conv-item--active" : ""}`}
              >
                <div className="conv-item-title">{session.title}</div>
                <div className="conv-item-meta">
                  <span>
                    {formatDistanceToNow(new Date(session.updated_at), {
                      addSuffix: true,
                    })}
                  </span>
                  <button
                    className="conv-delete-btn"
                    onClick={(e) => handleDelete(e, session.id)}
                    title="Delete chat"
                  >
                    ×
                  </button>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
